// §4.8 Calendars (`/planning/settings/calendars`, P3-H-1, D-386 rulings 2, 8
// and 9): the facility calendar, a four-week preview of the resolved working
// days, the shift patterns, the work-centre assignments and the exceptions.
// It reads on `planning.calendar.read` alone, so maintenance reads it whole and
// nothing here calls `GET /v1/planning/settings`; the facility's pattern comes
// from the preview (row 36). `planning.calendar.manage` edits, and the facility
// calendar is set through the settings PUT by a `planning.settings.manage`
// holder. On a phone the screen reads only, as cards.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
} from "../../../../components/states";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { pickableWorkCenters } from "../../../../lib/work-centers";
import {
  AssignDialog,
  EndAssignmentDialog,
  ExceptionDialog,
  PatternEditor,
  type CalendarNotice,
} from "../components/calendar-dialogs";
import {
  EXCEPTION_KINDS,
  EXCEPTION_WINDOW_MAX_DAYS,
  deleteAssignment,
  deleteCalendarException,
  putFacilityCalendar,
  useAssignments,
  useCalendarExceptions,
  useCalendarPreview,
  useCalendarRefresh,
  useCalendarWorkCenters,
  useShiftNames,
  useShiftPatterns,
  type CalendarException,
  type CalendarExceptionKind,
  type CalendarPreviewDay,
  type CalendarPreviewSegment,
  type ExceptionFilters,
  type ShiftPattern,
  type WorkCenter,
  type WorkCenterCalendar,
} from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { problemCode } from "../hooks/planning-queries";
import {
  checkWindow,
  factorIsClosed,
  facilityToday,
  formatFactor,
  formatHours,
  groupSegments,
  isCurrentAssignment,
  isFutureAssignment,
  weekdayName,
  weekdayRuns,
  workCenterLabel,
  zoneWall,
} from "../lib/calendar-lib";
import { currentMonday, useOffline } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { shortId } from "../../../../lib/short-id";

/** The chip each exception kind wears: red closes for a fault, amber for a plan, green adds. */
const KIND_CHIP: Readonly<Record<CalendarExceptionKind, ChipVariant>> = {
  holiday: "neutral",
  planned_maintenance: "warn",
  breakdown: "bad",
  extra_shift: "ok",
  overtime: "accent",
};

type Dialog =
  | { kind: "pattern"; pattern: ShiftPattern | undefined }
  | { kind: "assign" }
  | { kind: "end"; row: WorkCenterCalendar }
  | { kind: "exception" };

export function PlanningCalendarsScreen() {
  const { t, i18n } = useTranslation();
  const desktop = useDesktop();
  const offline = useOffline();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const holdsManage = perms.has("planning.calendar.manage");
  // Phone reads only (§4.8); offline keeps the controls and disables them.
  const canManage = holdsManage && desktop;
  const canSetFacility =
    desktop && perms.has("planning.settings.manage") && perms.has("planning.read");
  const tz = facility.timezone;
  const today = facilityToday(tz);

  const patterns = useShiftPatterns();
  const workCenters = useCalendarWorkCenters(facility.id);
  // A segment names its shift as production named it, not by its code.
  const shiftNames = useShiftNames(facility.id, perms.has("production.read"));
  const [dialog, setDialog] = useState<Dialog | undefined>(undefined);
  const [notice, setNotice] = useState<CalendarNotice | undefined>(undefined);

  // A switch of facility closes whatever was open for the last one; each card is
  // keyed by the facility, so its own filters start afresh too.
  useEffect(() => {
    setDialog(undefined);
    setNotice(undefined);
  }, [facility.id]);

  const wcById = useMemo(() => {
    const map = new Map<string, WorkCenter>();
    for (const wc of workCenters.data ?? []) map.set(wc.id, wc);
    return map;
  }, [workCenters.data]);
  // Every picker on the screen offers the active work centres alone; the
  // full list above still names a row that points at an archived one.
  const pickable = useMemo(() => pickableWorkCenters(workCenters.data), [workCenters.data]);
  const wcName = (id: string | null) => {
    if (id === null) return t("planning.calendars.exceptions.wholeFacility");
    const wc = wcById.get(id);
    return wc === undefined ? shortId(id) : workCenterLabel(wc, i18n.language);
  };

  const done = (next: CalendarNotice) => {
    setDialog(undefined);
    setNotice(next);
  };

  return (
    <section className="planning-screen planning-calendars" data-testid="planning-calendars">
      <PlanningHeader title={t("planning.calendars.title")} />
      {!holdsManage ? (
        <p className="planning-note" data-testid="planning-calendars-read-only">
          {t("planning.calendars.readOnly")}
        </p>
      ) : null}
      {holdsManage && !desktop ? (
        <p className="planning-note" data-testid="planning-calendars-phone">
          {t("planning.calendars.phoneReadOnly")}
        </p>
      ) : null}
      {canManage && offline ? (
        <p className="planning-note" role="status">
          {t("planning.common.offlineWrites")}
        </p>
      ) : null}
      {notice !== undefined ? (
        <Banner
          kind={notice.kind}
          data-testid="planning-calendars-notice"
          onDismiss={() => {
            setNotice(undefined);
          }}
          dismissLabel={t("planning.common.close")}
        >
          {notice.text}
        </Banner>
      ) : null}
      <FacilityAndPreview
        key={`preview-${facility.id}`}
        facilityId={facility.id}
        timezone={tz}
        today={today}
        workCenters={pickable}
        patterns={patterns.data}
        shiftNames={shiftNames}
        canSetFacility={canSetFacility}
        offline={offline}
        onNotice={setNotice}
      />
      <PatternsCard
        patterns={patterns}
        shiftNames={shiftNames}
        canManage={canManage}
        offline={offline}
        onAdd={() => {
          setDialog({ kind: "pattern", pattern: undefined });
        }}
        onOpen={(pattern) => {
          setDialog({ kind: "pattern", pattern });
        }}
      />
      <AssignmentsCard
        key={`assignments-${facility.id}`}
        facilityId={facility.id}
        today={today}
        workCenters={pickable}
        wcName={wcName}
        canManage={canManage}
        offline={offline}
        onAdd={() => {
          setDialog({ kind: "assign" });
        }}
        onEnd={(row) => {
          setDialog({ kind: "end", row });
        }}
        onNotice={setNotice}
      />
      <ExceptionsCard
        key={`exceptions-${facility.id}`}
        facilityId={facility.id}
        timezone={tz}
        today={today}
        workCenters={pickable}
        wcName={wcName}
        canManage={canManage}
        offline={offline}
        onAdd={() => {
          setDialog({ kind: "exception" });
        }}
        onNotice={setNotice}
      />
      {dialog?.kind === "pattern" ? (
        <PatternEditor
          pattern={dialog.pattern}
          canEdit={canManage && !offline}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={done}
        />
      ) : null}
      {dialog?.kind === "assign" ? (
        <AssignDialog
          facilityId={facility.id}
          today={today}
          workCenters={pickable}
          patterns={(patterns.data ?? []).filter((p) => p.status === "active")}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={done}
        />
      ) : null}
      {dialog?.kind === "end" ? (
        <EndAssignmentDialog
          facilityId={facility.id}
          row={dialog.row}
          today={today}
          workCenterName={wcName(dialog.row.work_center_id)}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={done}
        />
      ) : null}
      {dialog?.kind === "exception" ? (
        <ExceptionDialog
          facilityId={facility.id}
          timezone={tz}
          today={today}
          workCenters={pickable}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={done}
        />
      ) : null}
    </section>
  );
}

// ---- the facility calendar and the preview ----------------------------------------

function FacilityAndPreview({
  facilityId,
  timezone,
  today,
  workCenters,
  patterns,
  shiftNames,
  canSetFacility,
  offline,
  onNotice,
}: {
  facilityId: string;
  timezone: string;
  today: string;
  workCenters: readonly WorkCenter[];
  patterns: readonly ShiftPattern[] | undefined;
  shiftNames: ReadonlyMap<string, string>;
  canSetFacility: boolean;
  offline: boolean;
  onNotice: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const thisMonday = currentMonday(timezone);
  const [from, setFrom] = useState(thisMonday);
  const [wcId, setWcId] = useState<string | undefined>(undefined);

  const preview = useCalendarPreview(facilityId, from, wcId);
  const data = preview.data?.facility_id === facilityId ? preview.data : undefined;

  useEffect(() => {
    if (preview.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [preview.error, navigate]);

  // The facility's own pattern, which every preview carries whatever it previews.
  const [facilityPattern, setFacilityPattern] = useState<
    { id: string; code: string; name: string } | null | undefined
  >(undefined);
  useEffect(() => {
    if (data !== undefined) setFacilityPattern(data.facility_pattern);
  }, [data]);

  const sortedWcs = [...workCenters].sort((a, b) => a.code.localeCompare(b.code));
  const forbidden = preview.error instanceof RequestError && preview.error.httpStatus === 403;

  return (
    <>
      <FacilityCalendarCard
        facilityId={facilityId}
        current={facilityPattern}
        patterns={patterns}
        canSet={canSetFacility}
        offline={offline}
        onSaved={(saved) => {
          setFacilityPattern(saved);
          void refresh.preview(facilityId);
          void refresh.settings(facilityId);
          onNotice({
            kind: "ok",
            text:
              saved === null
                ? t("planning.calendars.saved.facilityCalendarCleared")
                : t("planning.calendars.saved.facilityCalendar", { name: saved.name }),
          });
        }}
      />
      <Card
        title={t("planning.calendars.preview.title")}
        data-testid="planning-calendar-preview-card"
      >
        <div className="filter-bar">
          <Field label={t("planning.calendars.preview.of")}>
            <Select
              value={wcId ?? ""}
              data-testid="planning-calendar-preview-scope"
              onChange={(e) => {
                setWcId(e.target.value === "" ? undefined : e.target.value);
              }}
            >
              <option value="">{t("planning.calendars.preview.facilityScope")}</option>
              {sortedWcs.map((wc) => (
                <option key={wc.id} value={wc.id}>
                  {workCenterLabel(wc, i18n.language)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="planning-window">
            <Button
              size="sm"
              data-testid="planning-calendar-earlier"
              onClick={() => {
                setFrom(addDaysIso(from, -28));
              }}
            >
              {t("planning.calendars.preview.earlier")}
            </Button>
            <Button
              size="sm"
              disabled={from === thisMonday}
              data-testid="planning-calendar-this-week"
              onClick={() => {
                setFrom(thisMonday);
              }}
            >
              {t("planning.calendars.preview.thisWeek")}
            </Button>
            <Button
              size="sm"
              data-testid="planning-calendar-later"
              onClick={() => {
                setFrom(addDaysIso(from, 28));
              }}
            >
              {t("planning.calendars.preview.later")}
            </Button>
          </div>
        </div>
        <p className="planning-hint" data-testid="planning-calendar-range">
          {t("planning.calendars.preview.range", {
            from: formatBusinessDate(from, i18n.language),
            to: formatBusinessDate(addDaysIso(from, 27), i18n.language),
            zone: timezone,
          })}
        </p>
        {forbidden ? (
          <ForbiddenState title={t("planning.access.title")} body={t("planning.access.screen")} />
        ) : preview.error !== null && !(preview.error instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("planning.calendars.preview.loadError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void preview.refetch()}
            requestId={requestIdOf(preview.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        ) : data === undefined || preview.isPending ? (
          <div className="planning-cal-skel" data-testid="planning-calendar-preview-loading">
            <CardSkeleton lines={6} label={t("planning.calendars.preview.loading")} />
          </div>
        ) : (
          <PreviewStrip
            days={data.days}
            exceptions={data.exceptions}
            timezone={data.time_zone}
            today={today}
            workCenterPreview={data.work_center_id !== null}
            shiftNames={shiftNames}
          />
        )}
      </Card>
    </>
  );
}

function FacilityCalendarCard({
  facilityId,
  current,
  patterns,
  canSet,
  offline,
  onSaved,
}: {
  facilityId: string;
  current: { id: string; code: string; name: string } | null | undefined;
  patterns: readonly ShiftPattern[] | undefined;
  canSet: boolean;
  offline: boolean;
  onSaved: (pattern: { id: string; code: string; name: string } | null) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<unknown>(undefined);
  useEffect(() => {
    setChoice(undefined);
    setProblem(undefined);
    setFailure(undefined);
  }, [facilityId, current?.id]);

  if (current === undefined) {
    return (
      <Card title={t("planning.calendars.facility.title")}>
        <CardSkeleton lines={2} label={t("planning.calendars.loading")} />
      </Card>
    );
  }

  const active = (patterns ?? []).filter((p) => p.status === "active");
  const selected = choice ?? current?.id ?? "";
  const changed = selected !== (current?.id ?? "");

  const onSave = async () => {
    setSaving(true);
    setProblem(undefined);
    setFailure(undefined);
    const next = selected === "" ? null : selected;
    try {
      await putFacilityCalendar(facilityId, next);
      const p = next === null ? null : active.find((x) => x.id === next);
      onSaved(p === undefined || p === null ? null : { id: p.id, code: p.code, name: p.name });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      if (code === "SHIFT_PATTERN_NOT_FOUND") setProblem("planning.calendars.facility.patternGone");
      else if (code === "PLANNING_SETTINGS_CHANGED")
        setProblem("planning.calendars.facility.changed");
      else setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={t("planning.calendars.facility.title")} data-testid="planning-facility-calendar">
      {current === null ? (
        <Banner kind="warn" data-testid="planning-calendars-no-facility-calendar">
          {t("planning.calendars.facility.none")}
        </Banner>
      ) : (
        <p className="planning-last" data-testid="planning-facility-calendar-current">
          {t("planning.calendars.facility.current", { name: current.name })}
        </p>
      )}
      <p className="planning-hint">{t("planning.calendars.facility.body")}</p>
      {canSet ? (
        <div className="planning-bulk">
          <Field label={t("planning.calendars.facility.pattern")}>
            <Select
              value={selected}
              disabled={saving || offline}
              data-testid="planning-facility-calendar-select"
              onChange={(e) => {
                setChoice(e.target.value);
                setProblem(undefined);
              }}
            >
              <option value="">{t("planning.calendars.facility.noCalendar")}</option>
              {active.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            variant="primary"
            busy={saving}
            busyLabel={t("planning.common.saving")}
            disabled={!changed || offline}
            data-testid="planning-facility-calendar-save"
            onClick={() => void onSave()}
          >
            {t("planning.calendars.facility.save")}
          </Button>
          {active.length === 0 && patterns !== undefined ? (
            <span className="planning-hint">{t("planning.calendars.facility.noPatterns")}</span>
          ) : null}
          {problem !== undefined ? (
            <span
              className="field-error"
              role="alert"
              data-testid="planning-facility-calendar-error"
            >
              {t(problem)}
            </span>
          ) : null}
          {failure !== undefined ? (
            <span
              className="field-error"
              role="alert"
              data-testid="planning-facility-calendar-error"
            >
              {t("planning.common.saveFailed", { requestId: requestIdOf(failure) ?? "-" })}
            </span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** A segment's wall times: the pattern's own, or an extra shift's read in the zone. */
function segmentTimes(seg: CalendarPreviewSegment, timezone: string): string {
  const start = seg.start ?? zoneWall(Date.parse(seg.starts_at), timezone)?.slice(11) ?? "";
  const end = seg.end ?? zoneWall(Date.parse(seg.ends_at), timezone)?.slice(11) ?? "";
  return `${start}–${end}`;
}

function exceptionName(row: CalendarException | undefined, t: TFunction): string {
  if (row === undefined) return "";
  const kind = t(`planning.calendars.exceptions.kinds.${row.kind}`);
  return row.note !== null && row.note.length > 0 ? `${kind} (${row.note})` : kind;
}

export function PreviewStrip({
  days,
  exceptions,
  timezone,
  today,
  workCenterPreview,
  shiftNames = new Map<string, string>(),
}: {
  days: readonly CalendarPreviewDay[];
  exceptions: readonly CalendarException[];
  timezone: string;
  today: string;
  workCenterPreview: boolean;
  /** Shift code to the name production gave it; an unnamed code shows as itself. */
  shiftNames?: ReadonlyMap<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const byId = new Map(exceptions.map((e) => [e.id, e]));
  const names = (ids: readonly string[]) =>
    ids
      .map((id) => exceptionName(byId.get(id), t))
      .filter((n) => n.length > 0)
      .join(", ");
  const someUncalendared = workCenterPreview && days.some((d) => !d.calendared);
  return (
    <>
      {someUncalendared ? (
        <p className="planning-note" data-testid="planning-calendar-wc-uncalendared">
          {t("planning.calendars.preview.uncalendaredWorkCenter")}
        </p>
      ) : null}
      <ol
        className="planning-cal-strip"
        data-testid="planning-calendar-preview"
        aria-label={t("planning.calendars.preview.label", {
          from: formatBusinessDate(days[0]?.date ?? today, i18n.language),
        })}
      >
        {days.map((day) => {
          const state = !day.calendared ? "uncalendared" : day.working ? "working" : "closed";
          const changedIds = day.exception_ids.filter(
            (id) => !day.closing_exception_ids.includes(id),
          );
          return (
            <li
              key={day.date}
              className={`planning-cal-day planning-cal-day--${state}${day.date === today ? " is-today" : ""}`}
              data-testid="planning-calendar-day"
              data-date={day.date}
              data-working={day.working ? "true" : "false"}
              data-calendared={day.calendared ? "true" : "false"}
            >
              <div className="planning-cal-day-head">
                <span className="planning-cal-date">
                  {weekdayName(day.weekday, i18n.language)}{" "}
                  {formatBusinessDate(day.date, i18n.language)}
                </span>
                <StatusChip
                  variant={state === "working" ? "ok" : state === "closed" ? "bad" : "neutral"}
                >
                  {t(`planning.calendars.preview.${state}`)}
                </StatusChip>
              </div>
              {day.working ? (
                <span className="planning-cal-hours" data-testid="planning-calendar-day-hours">
                  {t("planning.calendars.preview.effective", {
                    value: formatHours(day.effective_minutes, i18n.language),
                  })}
                </span>
              ) : null}
              {day.segments.length > 0 ? (
                <ul className="planning-cal-segments">
                  {day.segments.map((seg) => (
                    <li
                      key={seg.starts_at}
                      data-testid="planning-calendar-segment"
                      data-shift-code={seg.shift_code ?? ""}
                      data-efficiency={String(seg.efficiency_pct)}
                      data-crosses-midnight={seg.crosses_midnight ? "true" : "false"}
                      data-source={seg.source}
                      data-effective-minutes={String(seg.effective_minutes)}
                    >
                      <span className="planning-cal-shift">
                        {seg.source === "extra_shift"
                          ? t("planning.calendars.preview.extraShift")
                          : seg.shift_code === null
                            ? ""
                            : (shiftNames.get(seg.shift_code) ?? seg.shift_code)}
                      </span>
                      <span className="planning-cal-times">
                        {segmentTimes(seg, timezone)}
                        {seg.crosses_midnight
                          ? ` (${t("planning.calendars.preview.nextDay")})`
                          : ""}
                      </span>
                      <span className="planning-cal-eff">
                        {t("planning.calendars.preview.atEfficiency", {
                          pct: seg.efficiency_pct,
                        })}
                      </span>
                      <span className="planning-cal-eff">
                        {t("planning.calendars.preview.hours", {
                          value: formatHours(seg.effective_minutes, i18n.language),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {day.closing_exception_ids.length > 0 ? (
                <p className="planning-cal-why" data-testid="planning-calendar-closing">
                  {t("planning.calendars.preview.closedBy", {
                    names: names(day.closing_exception_ids),
                  })}
                </p>
              ) : null}
              {changedIds.length > 0 ? (
                <p className="planning-cal-why" data-testid="planning-calendar-changed">
                  {t("planning.calendars.preview.changedBy", { names: names(changedIds) })}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
    </>
  );
}

// ---- patterns ---------------------------------------------------------------------

type PatternStatusFilter = "active" | "archived" | "all";

function PatternsCard({
  patterns,
  shiftNames,
  canManage,
  offline,
  onAdd,
  onOpen,
}: {
  patterns: ReturnType<typeof useShiftPatterns>;
  shiftNames: ReadonlyMap<string, string>;
  canManage: boolean;
  offline: boolean;
  onAdd: () => void;
  onOpen: (pattern: ShiftPattern) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PatternStatusFilter>("active");
  useEffect(() => {
    if (patterns.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [patterns.error, navigate]);
  const all = patterns.data ?? [];
  const rows = all.filter((p) => status === "all" || p.status === status);

  const columns: DataTableColumn<ShiftPattern>[] = [
    {
      key: "code",
      header: t("planning.calendars.patterns.columns.code"),
      id: true,
      render: (p) => p.code,
    },
    { key: "name", header: t("planning.calendars.patterns.columns.name"), render: (p) => p.name },
    {
      key: "segments",
      header: t("planning.calendars.patterns.columns.segments"),
      render: (p) => (
        <ul className="planning-cal-groups">
          {groupSegments(p.segments).map((g) => (
            <li key={`${g.start}|${g.end}|${g.shift_code}|${String(g.efficiency_pct)}`}>
              {weekdayRuns(g.weekdays, i18n.language)} {g.start}–{g.end}{" "}
              {shiftNames.get(g.shift_code) ?? g.shift_code}{" "}
              {t("planning.calendars.preview.atEfficiency", { pct: g.efficiency_pct })}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: "status",
      header: t("planning.calendars.patterns.columns.status"),
      render: (p) => (
        <StatusChip variant={p.status === "active" ? "ok" : "neutral"}>
          {t(`planning.status.${p.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "actions",
      header: t("planning.calendars.patterns.columns.actions"),
      actions: true,
      render: (p) => (
        <Button
          size="sm"
          data-testid="planning-pattern-open"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(p);
          }}
        >
          {canManage ? t("planning.common.edit") : t("planning.common.view")}
        </Button>
      ),
    },
  ];

  const empty =
    all.length === 0 ? (
      <EmptyState
        title={t("planning.calendars.patterns.emptyTitle")}
        body={t("planning.calendars.patterns.emptyBody")}
        ctaLabel={canManage && !offline ? t("planning.calendars.patterns.add") : undefined}
        onCta={canManage && !offline ? onAdd : undefined}
      />
    ) : (
      <EmptyState
        title={t("planning.calendars.patterns.filteredEmptyTitle")}
        body={t("planning.calendars.patterns.filteredEmptyBody", {
          status: t(`planning.status.${status === "all" ? "active" : status}`),
        })}
        ctaLabel={t("planning.calendars.patterns.showAll")}
        onCta={() => {
          setStatus("all");
        }}
      />
    );

  return (
    <Card
      title={t("planning.calendars.patterns.title")}
      count={patterns.data !== undefined ? rows.length : undefined}
      data-testid="planning-patterns"
      action={
        canManage ? (
          <Button size="sm" disabled={offline} data-testid="planning-pattern-add" onClick={onAdd}>
            {t("planning.calendars.patterns.add")}
          </Button>
        ) : undefined
      }
    >
      <div className="filter-bar">
        <Field label={t("planning.calendars.patterns.status")}>
          <Select
            value={status}
            data-testid="planning-patterns-status"
            onChange={(e) => {
              setStatus(e.target.value as PatternStatusFilter);
            }}
          >
            <option value="active">{t("planning.status.active")}</option>
            <option value="archived">{t("planning.status.archived")}</option>
            <option value="all">{t("planning.common.all")}</option>
          </Select>
        </Field>
      </div>
      {patterns.error !== null && !(patterns.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("planning.calendars.patterns.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void patterns.refetch()}
          requestId={requestIdOf(patterns.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(p) => p.id}
          rowTestId="planning-pattern-row"
          rowProps={(p) => ({ "data-code": p.code, "data-status": p.status })}
          fit
          loading={patterns.isPending}
          loadingLabel={t("planning.calendars.patterns.loading")}
          empty={empty}
          caption={t("planning.calendars.patterns.title")}
          onRowClick={onOpen}
        />
      )}
    </Card>
  );
}

// ---- assignments ------------------------------------------------------------------

function AssignmentsCard({
  facilityId,
  today,
  workCenters,
  wcName,
  canManage,
  offline,
  onAdd,
  onEnd,
  onNotice,
}: {
  facilityId: string;
  today: string;
  workCenters: readonly WorkCenter[];
  wcName: (id: string | null) => string;
  canManage: boolean;
  offline: boolean;
  onAdd: () => void;
  onEnd: (row: WorkCenterCalendar) => void;
  onNotice: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const [wcFilter, setWcFilter] = useState<string | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [removing, setRemoving] = useState<string | undefined>(undefined);
  const page = cursors.length - 1;
  const list = useAssignments(facilityId, wcFilter, cursors[page]);
  useEffect(() => {
    setCursors([undefined]);
  }, [wcFilter]);
  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const rows = list.data?.items ?? [];

  const onRemove = async (row: WorkCenterCalendar) => {
    setRemoving(row.id);
    try {
      await deleteAssignment(row.id);
      onNotice({
        kind: "ok",
        text: t("planning.calendars.saved.removed", { workCenter: wcName(row.work_center_id) }),
      });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      onNotice({
        kind: "bad",
        text:
          code === "ASSIGNMENT_IN_EFFECT"
            ? t("planning.calendars.assignments.errors.inEffect")
            : code === "WORK_CENTER_CALENDAR_NOT_FOUND"
              ? t("planning.calendars.assignments.errors.gone")
              : t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }),
      });
    } finally {
      setRemoving(undefined);
      void refresh.assignments(facilityId);
      void refresh.preview(facilityId);
    }
  };

  const columns: DataTableColumn<WorkCenterCalendar>[] = [
    {
      key: "workCenter",
      header: t("planning.calendars.assignments.columns.workCenter"),
      id: true,
      render: (row) => wcName(row.work_center_id),
    },
    {
      key: "pattern",
      header: t("planning.calendars.assignments.columns.pattern"),
      render: (row) => row.shift_pattern_name,
    },
    {
      key: "from",
      header: t("planning.calendars.assignments.columns.from"),
      render: (row) => formatBusinessDate(row.valid_from, i18n.language),
    },
    {
      key: "to",
      header: t("planning.calendars.assignments.columns.to"),
      render: (row) =>
        row.valid_to === null
          ? t("planning.calendars.assignments.openEnded")
          : formatBusinessDate(row.valid_to, i18n.language),
    },
    {
      key: "state",
      header: t("planning.calendars.assignments.columns.state"),
      render: (row) =>
        isFutureAssignment(row.valid_from, today) ? (
          <StatusChip variant="accent">{t("planning.calendars.assignments.future")}</StatusChip>
        ) : isCurrentAssignment(row.valid_from, row.valid_to, today) ? (
          <StatusChip variant="ok">{t("planning.calendars.assignments.inEffect")}</StatusChip>
        ) : (
          <StatusChip variant="neutral">{t("planning.calendars.assignments.ended")}</StatusChip>
        ),
    },
    {
      key: "warning",
      header: t("planning.calendars.assignments.columns.warning"),
      render: (row) =>
        row.missing_shift_codes.length > 0 ? (
          <span
            className="planning-cal-warning"
            title={t("planning.calendars.assignments.missingCodesHint")}
            data-testid="planning-assignment-warning"
          >
            <StatusChip variant="warn">
              {t("planning.calendars.assignments.missingCodes", {
                codes: row.missing_shift_codes.join(", "),
              })}
            </StatusChip>
          </span>
        ) : (
          <span className="planning-hint">{t("planning.calendars.assignments.shiftsOk")}</span>
        ),
    },
  ];
  if (canManage) {
    columns.push({
      key: "actions",
      header: t("planning.calendars.assignments.columns.actions"),
      actions: true,
      render: (row) =>
        isFutureAssignment(row.valid_from, today) ? (
          <Button
            size="sm"
            variant="danger"
            busy={removing === row.id}
            busyLabel={t("planning.common.saving")}
            disabled={offline || removing !== undefined}
            data-testid="planning-assignment-remove"
            onClick={(e) => {
              e.stopPropagation();
              void onRemove(row);
            }}
          >
            {t("planning.calendars.assignments.remove")}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={offline || removing !== undefined}
            data-testid="planning-assignment-end"
            onClick={(e) => {
              e.stopPropagation();
              onEnd(row);
            }}
          >
            {t("planning.calendars.assignments.end")}
          </Button>
        ),
    });
  }

  const sortedWcs = [...workCenters].sort((a, b) => a.code.localeCompare(b.code));

  return (
    <Card
      title={t("planning.calendars.assignments.title")}
      data-testid="planning-assignments"
      action={
        canManage ? (
          <Button
            size="sm"
            disabled={offline}
            data-testid="planning-assignment-add"
            onClick={onAdd}
          >
            {t("planning.calendars.assignments.add")}
          </Button>
        ) : undefined
      }
    >
      <div className="filter-bar">
        <Field label={t("planning.calendars.assignments.filter")}>
          <Select
            value={wcFilter ?? ""}
            data-testid="planning-assignments-work-center"
            onChange={(e) => {
              setWcFilter(e.target.value === "" ? undefined : e.target.value);
            }}
          >
            <option value="">{t("planning.calendars.assignments.allWorkCenters")}</option>
            {sortedWcs.map((wc) => (
              <option key={wc.id} value={wc.id}>
                {workCenterLabel(wc, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {list.error !== null && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("planning.calendars.assignments.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId="planning-assignment-row"
          rowProps={(row) => ({
            "data-work-center-id": row.work_center_id,
            "data-pattern-code": row.shift_pattern_code,
            "data-valid-from": row.valid_from,
          })}
          fit
          loading={list.isPending}
          loadingLabel={t("planning.calendars.assignments.loading")}
          empty={
            <EmptyState
              title={t("planning.calendars.assignments.emptyTitle")}
              body={
                wcFilter === undefined
                  ? t("planning.calendars.assignments.emptyBody")
                  : t("planning.calendars.assignments.filteredEmptyBody", {
                      workCenter: wcName(wcFilter),
                    })
              }
              ctaLabel={canManage && !offline ? t("planning.calendars.assignments.add") : undefined}
              onCta={canManage && !offline ? onAdd : undefined}
            />
          }
          caption={t("planning.calendars.assignments.title")}
          pager={pagerOf(t, page, list.data?.next_cursor ?? null, cursors, setCursors)}
        />
      )}
    </Card>
  );
}

function pagerOf(
  t: TFunction,
  page: number,
  nextCursor: string | null,
  cursors: (string | undefined)[],
  setCursors: (next: (string | undefined)[]) => void,
) {
  return {
    range: t("planning.common.page", { page: page + 1 }),
    page: page + 1,
    onPage: (n: number) => {
      if (n > page + 1 && nextCursor !== null) setCursors([...cursors, nextCursor]);
      else if (n < page + 1) setCursors(cursors.slice(0, -1));
    },
    prevLabel: t("planning.common.previous"),
    nextLabel: t("planning.common.next"),
    hasPrev: page > 0,
    hasNext: nextCursor !== null,
  };
}

// ---- exceptions -------------------------------------------------------------------

/** An exception's two edges for reading: whole facility days, or two local timestamps. */
export function exceptionSpan(
  row: Pick<CalendarException, "starts_at" | "ends_at">,
  timezone: string,
): { allDay: true; first: string; last: string } | { allDay: false } {
  const start = zoneWall(Date.parse(row.starts_at), timezone);
  const end = zoneWall(Date.parse(row.ends_at), timezone);
  if (start?.endsWith("T00:00") === true && end?.endsWith("T00:00") === true) {
    const first = start.slice(0, 10);
    const last = addDaysIso(end.slice(0, 10), -1);
    if (last >= first) return { allDay: true, first, last };
  }
  return { allDay: false };
}

function ExceptionsCard({
  facilityId,
  timezone,
  today,
  workCenters,
  wcName,
  canManage,
  offline,
  onAdd,
  onNotice,
}: {
  facilityId: string;
  timezone: string;
  today: string;
  workCenters: readonly WorkCenter[];
  wcName: (id: string | null) => string;
  canManage: boolean;
  offline: boolean;
  onAdd: () => void;
  onNotice: (notice: CalendarNotice) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useCalendarRefresh();
  const defaults = (): ExceptionFilters => ({ from: today, to: addDaysIso(today, 365) });
  const [filters, setFilters] = useState<ExceptionFilters>(defaults);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [removing, setRemoving] = useState<string | undefined>(undefined);
  useEffect(() => {
    setCursors([undefined]);
  }, [filters]);
  const windowProblem = checkWindow(filters.from, filters.to, EXCEPTION_WINDOW_MAX_DAYS);
  const page = cursors.length - 1;
  const list = useCalendarExceptions(
    facilityId,
    filters,
    cursors[page],
    windowProblem === undefined,
  );
  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);
  const rows = list.data?.items ?? [];
  const filtered = filters.kind !== undefined || filters.workCenterId !== undefined;

  const onRemove = async (row: CalendarException) => {
    setRemoving(row.id);
    const kind = t(`planning.calendars.exceptions.kinds.${row.kind}`);
    try {
      await deleteCalendarException(row.id);
      onNotice({ kind: "ok", text: t("planning.calendars.saved.exceptionRemoved", { kind }) });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      onNotice({
        kind: "bad",
        text:
          problemCode(error) === "CALENDAR_EXCEPTION_NOT_FOUND"
            ? t("planning.calendars.exceptions.errors.gone")
            : t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }),
      });
    } finally {
      setRemoving(undefined);
      void refresh.exceptions(facilityId);
      void refresh.preview(facilityId);
    }
  };

  const edge = (row: CalendarException, which: "start" | "end"): ReactNode => {
    const span = exceptionSpan(row, timezone);
    if (span.allDay)
      return formatBusinessDate(which === "start" ? span.first : span.last, i18n.language);
    return formatTimestamp(
      which === "start" ? row.starts_at : row.ends_at,
      i18n.language,
      timezone,
    );
  };

  const columns: DataTableColumn<CalendarException>[] = [
    {
      key: "kind",
      header: t("planning.calendars.exceptions.columns.kind"),
      render: (row) => (
        <StatusChip variant={KIND_CHIP[row.kind]}>
          {t(`planning.calendars.exceptions.kinds.${row.kind}`)}
        </StatusChip>
      ),
    },
    {
      key: "scope",
      header: t("planning.calendars.exceptions.columns.scope"),
      render: (row) => wcName(row.work_center_id),
    },
    {
      key: "starts",
      header: t("planning.calendars.exceptions.columns.starts"),
      render: (row) => edge(row, "start"),
    },
    {
      key: "ends",
      header: t("planning.calendars.exceptions.columns.ends"),
      render: (row) => edge(row, "end"),
    },
    {
      key: "factor",
      header: t("planning.calendars.exceptions.columns.factor"),
      render: (row) =>
        factorIsClosed(row.capacity_factor)
          ? t("planning.calendars.exceptions.closed")
          : t("planning.calendars.exceptions.factor", {
              factor: formatFactor(row.capacity_factor, i18n.language),
            }),
    },
    {
      key: "note",
      header: t("planning.calendars.exceptions.columns.note"),
      render: (row) => <span className="planning-product-name">{row.note ?? ""}</span>,
    },
  ];
  if (canManage) {
    columns.push({
      key: "actions",
      header: t("planning.calendars.exceptions.columns.actions"),
      actions: true,
      render: (row) => (
        <Button
          size="sm"
          variant="danger"
          busy={removing === row.id}
          busyLabel={t("planning.common.saving")}
          disabled={offline || removing !== undefined}
          data-testid="planning-exception-remove"
          onClick={(e) => {
            e.stopPropagation();
            void onRemove(row);
          }}
        >
          {t("planning.calendars.exceptions.remove")}
        </Button>
      ),
    });
  }

  const describe = () =>
    [
      filters.kind !== undefined ? t(`planning.calendars.exceptions.kinds.${filters.kind}`) : null,
      filters.workCenterId !== undefined ? wcName(filters.workCenterId) : null,
    ]
      .filter((x): x is string => x !== null)
      .join(", ");

  const sortedWcs = [...workCenters].sort((a, b) => a.code.localeCompare(b.code));

  return (
    <Card
      title={t("planning.calendars.exceptions.title")}
      data-testid="planning-exceptions"
      action={
        canManage ? (
          <Button size="sm" disabled={offline} data-testid="planning-exception-add" onClick={onAdd}>
            {t("planning.calendars.exceptions.add")}
          </Button>
        ) : undefined
      }
    >
      <div className="filter-bar">
        <Field label={t("planning.calendars.exceptions.from")}>
          <input
            type="date"
            value={filters.from}
            data-testid="planning-exceptions-from"
            onChange={(e) => {
              setFilters({ ...filters, from: e.target.value });
            }}
          />
        </Field>
        <Field label={t("planning.calendars.exceptions.to")}>
          <input
            type="date"
            value={filters.to}
            data-testid="planning-exceptions-to"
            onChange={(e) => {
              setFilters({ ...filters, to: e.target.value });
            }}
          />
        </Field>
        <Field label={t("planning.calendars.exceptions.kind")}>
          <Select
            value={filters.kind ?? ""}
            data-testid="planning-exceptions-kind"
            onChange={(e) => {
              setFilters({
                ...filters,
                kind: e.target.value === "" ? undefined : (e.target.value as CalendarExceptionKind),
              });
            }}
          >
            <option value="">{t("planning.calendars.exceptions.allKinds")}</option>
            {EXCEPTION_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`planning.calendars.exceptions.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("planning.calendars.exceptions.scope")}>
          <Select
            value={filters.workCenterId ?? ""}
            data-testid="planning-exceptions-work-center"
            onChange={(e) => {
              setFilters({
                ...filters,
                workCenterId: e.target.value === "" ? undefined : e.target.value,
              });
            }}
          >
            <option value="">{t("planning.calendars.exceptions.allScopes")}</option>
            {sortedWcs.map((wc) => (
              <option key={wc.id} value={wc.id}>
                {workCenterLabel(wc, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {windowProblem !== undefined ? (
        <p className="field-error" role="alert" data-testid="planning-exceptions-window-error">
          {t(`planning.calendars.exceptions.window.${windowProblem}`)}
        </p>
      ) : list.error !== null && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("planning.calendars.exceptions.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId="planning-exception-row"
          rowProps={(row) => ({
            "data-kind": row.kind,
            "data-work-center-id": row.work_center_id ?? "",
            "data-note": row.note ?? "",
          })}
          fit
          loading={list.isPending}
          loadingLabel={t("planning.calendars.exceptions.loading")}
          empty={
            filtered ? (
              <EmptyState
                title={t("planning.calendars.exceptions.emptyTitle")}
                body={t("planning.calendars.exceptions.filteredEmptyBody", { filters: describe() })}
                ctaLabel={t("planning.common.clearFilters")}
                onCta={() => {
                  setFilters({ from: filters.from, to: filters.to });
                }}
              />
            ) : (
              <EmptyState
                title={t("planning.calendars.exceptions.emptyTitle")}
                body={t("planning.calendars.exceptions.emptyBody")}
                ctaLabel={
                  canManage && !offline ? t("planning.calendars.exceptions.add") : undefined
                }
                onCta={canManage && !offline ? onAdd : undefined}
              />
            )
          }
          caption={t("planning.calendars.exceptions.title")}
          pager={pagerOf(t, page, list.data?.next_cursor ?? null, cursors, setCursors)}
        />
      )}
    </Card>
  );
}
